import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, LogIn, KeyRound } from "lucide-react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

/**
 * Phase 2b2 — Resolve `(mssp_slug, tenant_slug)` from the current
 * hostname so that a deep link `https://<tenant>.<mssp>.ward3.io`
 * targets the right user even when emails collide across MSSPs.
 *
 * Layout assumptions (matching the planned Phase 8 admin SPA):
 *   - `<tenant>.<mssp>.ward3.io`        → tenant scope
 *   - `<mssp>.ward3.io`                 → MSSP scope (no tenant)
 *   - `app.ward3.io` / `localhost`      → no resolution; user types
 *                                          slugs by hand if needed.
 *
 * The function is exported for unit tests; it does not touch the DOM.
 */
export function resolveSlugsFromHost(host) {
  if (!host) return { mssp_slug: null, tenant_slug: null };
  const lower = host.toLowerCase().split(":")[0]; // strip :port
  // Localhost / IPs / single-label hosts → no resolution.
  if (lower === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(lower)) {
    return { mssp_slug: null, tenant_slug: null };
  }
  const parts = lower.split(".");
  // Heuristic: reserve last 2 parts as the apex (e.g. `ward3.io`,
  // `localhost`, `example.com`). Anything before that = subdomain
  // labels we may match.
  if (parts.length < 3) return { mssp_slug: null, tenant_slug: null };
  // Reserved labels that mean "no tenant/MSSP scope by convention" —
  // matches what the Phase 8 admin SPA will host.
  const RESERVED = new Set(["app", "www", "api", "admin"]);
  const subdomain = parts.slice(0, parts.length - 2);
  if (subdomain.length === 1) {
    const [label] = subdomain;
    if (RESERVED.has(label)) return { mssp_slug: null, tenant_slug: null };
    return { mssp_slug: label, tenant_slug: null };
  }
  if (subdomain.length >= 2) {
    const [tenantLabel, msspLabel] = subdomain.slice(-2);
    if (RESERVED.has(msspLabel)) return { mssp_slug: null, tenant_slug: null };
    return { mssp_slug: msspLabel, tenant_slug: tenantLabel };
  }
  return { mssp_slug: null, tenant_slug: null };
}

export default function Login() {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || "/";

  // Resolve slugs once per mount; the hostname doesn't change during a
  // session so memoising is enough.
  const { mssp_slug, tenant_slug } = useMemo(() => {
    if (typeof window === "undefined") {
      return { mssp_slug: null, tenant_slug: null };
    }
    return resolveSlugsFromHost(window.location.hostname);
  }, []);

  // Manual override fields for the cases where the host carries no
  // slug (developer hitting `localhost`, ops tunnelling through
  // `app.ward3.io`). Pre-fill from the resolved subdomain so the
  // dev path stays one-step.
  const [msspSlugInput, setMsspSlugInput] = useState(mssp_slug ?? "");
  const [tenantSlugInput, setTenantSlugInput] = useState(tenant_slug ?? "");
  const showSlugFields = !mssp_slug; // host gave us nothing → ask user.

  // Phase 6a — fetch /auth/oidc-config au mount + à chaque change
  // de slug pour décider d'afficher le formulaire local ou un
  // bouton "Sign in with <IdP>". L'endpoint est public — pas
  // d'auth requise. Erreurs réseau silencieuses → fallback local
  // (UX friendly, pas de blocage si l'API est down au boot).
  const [oidcConfig, setOidcConfig] = useState({ has_oidc: false, button_label: null });
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    const m = (msspSlugInput || mssp_slug || "").trim();
    const t = (tenantSlugInput || tenant_slug || "").trim();
    if (m) params.set("mssp_slug", m);
    if (t) params.set("tenant_slug", t);
    if (!m) {
      // Pas de slug → pas la peine d'appeler. Reste en mode local.
      setOidcConfig({ has_oidc: false, button_label: null });
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/auth/oidc-config?${params.toString()}`);
        if (!cancelled) setOidcConfig(res.data ?? { has_oidc: false, button_label: null });
      } catch {
        if (!cancelled) setOidcConfig({ has_oidc: false, button_label: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [msspSlugInput, tenantSlugInput, mssp_slug, tenant_slug]);

  // Permet à l'opérateur d'override OIDC en cas de break-glass via
  // `?local=1` dans l'URL — utile si l'IdP est down et qu'un
  // platform_admin doit se connecter en local.
  const breakGlassLocal = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("local") === "1";
  }, []);
  const showOidcButton = oidcConfig.has_oidc && !breakGlassLocal;

  const handleOidcLogin = () => {
    // Phase 6b — redirige vers `/auth/oidc/:mssp_slug/start`. Le
    // backend pose un cookie state httpOnly+secure (HMAC signé,
    // TTL 5 min) puis 302 vers l'IdP. Au retour callback, le user
    // est connecté avec un JWT v2 (auth_method = "oidc").
    const m = (msspSlugInput || mssp_slug || "").trim();
    if (!m) {
      setError(t("login.oidc_mssp_required"));
      return;
    }
    const t = (tenantSlugInput || tenant_slug || "").trim();
    const qs = t ? `?tenant_slug=${encodeURIComponent(t)}` : "";
    // Hard navigation (pas axios) pour permettre les redirects
    // 302 vers l'IdP qui sortent du domaine de la SPA.
    window.location.assign(`/auth/oidc/${encodeURIComponent(m)}/start${qs}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, {
        mssp_slug: msspSlugInput.trim() || null,
        tenant_slug: tenantSlugInput.trim() || null,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Le backend renvoie des codes typés (cf. `AuthError` —
      // `invalid_credentials`, `unauthorized`, `internal`). On affiche
      // une chaîne traduite via le code plutôt que de propager le
      // `message` brut (qui restait en EN même quand l'UI est FR, et
      // disait historiquement "token is invalid or expired" sur un
      // simple mauvais password — confusing).
      const code = err.response?.data?.error;
      const status = err.response?.status;
      if (code === "invalid_credentials" || status === 401) {
        setError(t("login.error_invalid"));
      } else if (status >= 500) {
        setError(t("login.error_server"));
      } else {
        setError(t("login.generic_failure"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: "var(--color-bg)" }}
    >
      <form
        onSubmit={submit}
        className="rounded-2xl border p-6 sm:p-8 w-full max-w-sm space-y-4"
        style={{
          background: "var(--color-surface)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Shield size={32} color="#00d4ff" />
          <div>
            <h1
              className="text-lg font-bold tracking-widest"
              style={{ color: "#00d4ff" }}
            >
              Ward³
            </h1>
            <p className="text-xs" style={{ color: "#64748b" }}>
              Adversarial Neural Mediator
            </p>
          </div>
        </div>

        {mssp_slug && (
          <div
            className="text-xs px-3 py-2 rounded"
            style={{
              background: "#00d4ff10",
              color: "#94a3b8",
              border: "1px solid #00d4ff20",
            }}
            data-testid="resolved-scope"
          >
            {tenant_slug
              ? t("login.tenant_scope", { tenant: tenant_slug, mssp: mssp_slug })
              : t("login.mssp_scope", { mssp: mssp_slug })}
          </div>
        )}

        {/* Phase 6a — bouton OIDC affiché si l'IdP du tenant est
            configuré côté backend. Phase 6b implémentera le flow
            PKCE start/callback. En attendant le bouton est cliquable
            mais affiche un message explicatif. */}
        {showOidcButton && (
          <div className="space-y-2" data-testid="oidc-section">
            <button
              type="button"
              onClick={handleOidcLogin}
              className="w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
              style={{
                background: "#10b98120",
                color: "#10b981",
                border: "1px solid #10b98140",
              }}
            >
              <KeyRound size={14} />
              {oidcConfig.button_label ?? t("login.sso_button_default")}
            </button>
            <div className="text-[10px] text-center" style={{ color: "#64748b" }}>
              {t("login.break_glass_prefix")}{" "}
              <a
                href={`${window.location.pathname}?local=1`}
                className="underline"
                style={{ color: "#94a3b8" }}
              >
                ?local=1
              </a>
              {t("login.break_glass_suffix")}
            </div>
            <div
              className="flex items-center gap-2 text-[10px]"
              style={{ color: "#475569" }}
            >
              <div className="flex-1 h-px" style={{ background: "#1f2937" }} />
              <span>{t("login.sso_divider")}</span>
              <div className="flex-1 h-px" style={{ background: "#1f2937" }} />
            </div>
          </div>
        )}

        {showSlugFields && (
          <>
            <label className="block space-y-1">
              <span className="text-xs" style={{ color: "#94a3b8" }}>
                {t("login.mssp_slug_label")}
              </span>
              <input
                type="text"
                value={msspSlugInput}
                onChange={(e) => setMsspSlugInput(e.target.value)}
                autoComplete="organization"
                placeholder={t("login.mssp_slug_placeholder")}
                className="w-full px-3 py-2 rounded-lg outline-hidden text-sm focus:ring-2 focus:ring-[#00d4ff]"
                style={{
                  background: "#0a0e1a",
                  border: "1px solid var(--color-border)",
                  color: "#e2e8f0",
                }}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs" style={{ color: "#94a3b8" }}>
                {t("login.tenant_slug_label")}
              </span>
              <input
                type="text"
                value={tenantSlugInput}
                onChange={(e) => setTenantSlugInput(e.target.value)}
                placeholder={t("login.tenant_slug_placeholder")}
                className="w-full px-3 py-2 rounded-lg outline-hidden text-sm focus:ring-2 focus:ring-[#00d4ff]"
                style={{
                  background: "#0a0e1a",
                  border: "1px solid var(--color-border)",
                  color: "#e2e8f0",
                }}
              />
            </label>
          </>
        )}

        <label className="block space-y-1">
          <span className="text-xs" style={{ color: "#94a3b8" }}>
            {t("login.email")}
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            autoComplete="email"
            className="w-full px-3 py-2 rounded-lg outline-hidden text-sm focus:ring-2 focus:ring-[#00d4ff]"
            style={{
              background: "#0a0e1a",
              border: "1px solid var(--color-border)",
              color: "#e2e8f0",
            }}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-xs" style={{ color: "#94a3b8" }}>
            {t("login.password")}
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full px-3 py-2 rounded-lg outline-hidden text-sm focus:ring-2 focus:ring-[#00d4ff]"
            style={{
              background: "#0a0e1a",
              border: "1px solid var(--color-border)",
              color: "#e2e8f0",
            }}
          />
        </label>

        {error && (
          <div
            className="text-xs px-3 py-2 rounded"
            style={{
              background: "#ef444420",
              color: "#ef4444",
              border: "1px solid #ef444440",
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
          style={{
            background: loading ? "#1f2937" : "#00d4ff20",
            color: loading ? "#64748b" : "#00d4ff",
            border:
              "1px solid " + (loading ? "#1f2937" : "#00d4ff40"),
          }}
        >
          <LogIn size={14} />
          {loading ? t("login.submitting") : t("login.submit")}
        </button>

        {/* Sélecteur de langue accessible avant authentification —
            persiste en localStorage, transmis ensuite au serveur après
            login via PATCH /auth/me/preferences. */}
        <div className="flex justify-center pt-2">
          <LanguageSwitcher compact />
        </div>
      </form>
    </div>
  );
}
